import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { useApi } from '../../../hooks/useApi';

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2">
        <strong>{label}:</strong>{' '}
        <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {value || '—'}
        </Typography>
      </Typography>
      {value && (
        <Tooltip title={copied ? 'Copied!' : 'Copy'}>
          <IconButton size="small" onClick={handleCopy}>
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function InfoCard({ title, children }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      {children}
    </Paper>
  );
}

function AdminUserDetails() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const queryClient = useQueryClient();
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [extensionDays, setExtensionDays] = useState(14);
  const [extensionReason, setExtensionReason] = useState('');

  const { data: userData, isLoading, error, isError } = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () => api.get(`/api/admin/users/${userId}`),
  });

  const { data: freeMinutesData } = useQuery({
    queryKey: ['admin-free-minutes', userId],
    queryFn: () => api.get(`/api/admin/free-minutes/${userId}`),
    enabled: !!userId,
  });

  const { data: roomStatsData } = useQuery({
    queryKey: ['admin-room-stats', userId],
    queryFn: () => api.get(`/api/admin/users/${userId}/room-stats`),
    enabled: !!userId,
  });

  const extendTrialMutation = useMutation({
    mutationFn: (body) => api.post('/api/admin/extend-trial', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      setTrialDialogOpen(false);
      setExtensionDays(14);
      setExtensionReason('');
    },
  });

  // Metadata management state
  const [editableMetadata, setEditableMetadata] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [showAddRow, setShowAddRow] = useState(false);

  // Sync editable metadata from server data
  useEffect(() => {
    if (userData?.user?.metadata) {
      setEditableMetadata({ ...userData.user.metadata });
    } else {
      setEditableMetadata({});
    }
  }, [userData]);

  const metadataMutation = useMutation({
    mutationFn: (metadata) => api.put(`/api/admin/users/${userId}/metadata`, { metadata }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', userId] });
      setEditingKey(null);
      setShowAddRow(false);
      setNewKey('');
      setNewValue('');
    },
  });

  const isDirty = useMemo(() => {
    const serverMeta = userData?.user?.metadata || {};
    return JSON.stringify(editableMetadata) !== JSON.stringify(serverMeta);
  }, [editableMetadata, userData]);

  const formatValue = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  const parseValue = (str) => {
    const trimmed = str.trim();
    if (trimmed === '') return '';
    // Try parsing as JSON for objects/arrays/numbers/booleans
    try {
      const parsed = JSON.parse(trimmed);
      return parsed;
    } catch {
      return trimmed;
    }
  };

  const handleStartEdit = (key) => {
    setEditingKey(key);
    setEditValue(formatValue(editableMetadata[key]));
  };

  const handleConfirmEdit = (key) => {
    setEditableMetadata((prev) => ({ ...prev, [key]: parseValue(editValue) }));
    setEditingKey(null);
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleDeleteKey = (key) => {
    setEditableMetadata((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleAddField = () => {
    const trimmedKey = newKey.trim();
    if (!trimmedKey || trimmedKey.startsWith('$') || trimmedKey.includes('.')) return;
    setEditableMetadata((prev) => ({ ...prev, [trimmedKey]: parseValue(newValue) }));
    setNewKey('');
    setNewValue('');
    setShowAddRow(false);
  };

  const handleSaveMetadata = () => {
    metadataMutation.mutate(editableMetadata);
  };

  const handleResetMetadata = () => {
    setEditableMetadata({ ...(userData?.user?.metadata || {}) });
    setEditingKey(null);
    setShowAddRow(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error?.message || 'Failed to load user'}</Alert>
      </Box>
    );
  }

  const user = userData?.user;
  if (!user) return null;

  const stripeInfo = user.stripeInfo || {};
  const trialEndsAt = stripeInfo.trialEndsAt ? dayjs(stripeInfo.trialEndsAt) : null;
  const trialActive = trialEndsAt && trialEndsAt.isAfter(dayjs());
  const freeMinutes = freeMinutesData?.freeMinutes;
  const roomStats = roomStatsData;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/admin/users')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">{user.name || user.email}</Typography>
      </Box>
      <Grid container spacing={2}>
        {/* Basic Info */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <InfoCard title="Basic Info">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <CopyField label="ID" value={user._id} />
              <Typography variant="body2"><strong>Name:</strong> {user.name || '—'}</Typography>
              <CopyField label="Email" value={user.email} />
              <Typography variant="body2"><strong>Phone:</strong> {user.phone || '—'}</Typography>
              <Typography variant="body2" component="div">
                <strong>Active:</strong>{' '}
                <Chip label={user.active !== false ? 'Yes' : 'No'} color={user.active !== false ? 'success' : 'error'} size="small" />
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2"><strong>Roles:</strong></Typography>
                {[...new Set(user.roles)].map((role) => (
                  <Chip key={role} label={role} size="small" variant="outlined" />
                ))}
              </Box>
              <Typography variant="body2"><strong>Created:</strong> {user.created ? dayjs(user.created).format('MMM D, YYYY h:mm A') : '—'}</Typography>
            </Box>
          </InfoCard>
        </Grid>

        {/* Account Info */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <InfoCard title="Account Info">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <CopyField label="API Key" value={user.apiKey} />
              <Typography variant="body2"><strong>Organization:</strong> {user.organization || '—'}</Typography>
              <CopyField label="Stripe Customer ID" value={user.stripeCustomerId} />
              <Typography variant="body2"><strong>Subscription:</strong> {stripeInfo.subscriptionStatus || '—'}</Typography>
              <Typography variant="body2"><strong>Plan:</strong> {stripeInfo.planName || '—'}</Typography>
            </Box>
          </InfoCard>
        </Grid>

        {/* Brand Options */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <InfoCard title="Brand Options">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2"><strong>Brand Name:</strong> {user.brandName || '—'}</Typography>
              <Typography variant="body2"><strong>Brand Org ID:</strong> {user.brandOrgId || '—'}</Typography>
              {user.brandOptions && (
                <>
                  <Typography variant="body2"><strong>Logo URL:</strong> {user.brandOptions.logoUrl || '—'}</Typography>
                  <Typography variant="body2"><strong>Primary Color:</strong> {user.brandOptions.primaryColor || '—'}</Typography>
                </>
              )}
            </Box>
          </InfoCard>
        </Grid>

        {/* Trial Management */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <InfoCard title="Trial Management">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" component="div">
                <strong>Trial Status:</strong>{' '}
                <Chip
                  label={trialActive ? 'Active' : 'Expired/None'}
                  color={trialActive ? 'success' : 'default'}
                  size="small"
                />
              </Typography>
              <Typography variant="body2">
                <strong>Trial Ends:</strong>{' '}
                {trialEndsAt ? trialEndsAt.format('MMM D, YYYY h:mm A') : '—'}
              </Typography>
              {trialActive && (
                <Typography variant="body2">
                  <strong>Days Remaining:</strong> {trialEndsAt.diff(dayjs(), 'day')}
                </Typography>
              )}
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1, alignSelf: 'flex-start' }}
                onClick={() => setTrialDialogOpen(true)}
              >
                Extend Trial
              </Button>
              {stripeInfo.trialExtensions?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Extension History:</Typography>
                  {stripeInfo.trialExtensions.map((ext, i) => (
                    <Typography
                      key={i}
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "text.secondary"
                      }}>
                      +{ext.days}d by {ext.extendedBy} on {dayjs(ext.extendedAt).format('MMM D, YYYY')}
                      {ext.reason && ` — ${ext.reason}`}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </InfoCard>
        </Grid>

        {/* Auto Recharge */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <InfoCard title="Auto Recharge">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" component="div">
                <strong>Enabled:</strong>{' '}
                <Chip
                  label={stripeInfo.autoRecharge?.enabled ? 'Yes' : 'No'}
                  color={stripeInfo.autoRecharge?.enabled ? 'success' : 'default'}
                  size="small"
                />
              </Typography>
              <Typography variant="body2"><strong>Threshold:</strong> ${stripeInfo.autoRecharge?.threshold || 0}</Typography>
              <Typography variant="body2"><strong>Amount:</strong> ${stripeInfo.autoRecharge?.amount || 0}</Typography>
            </Box>
          </InfoCard>
        </Grid>

        {/* Room Stats */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <InfoCard title="Room Stats">
            {roomStats ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2"><strong>Total Rooms:</strong> {roomStats.totalRooms ?? '—'}</Typography>
                <Typography variant="body2"><strong>Active Rooms:</strong> {roomStats.activeRooms ?? 0}</Typography>
                <Typography variant="body2">
                  <strong>Last Room:</strong>{' '}
                  {roomStats.lastRoomCreated ? dayjs(roomStats.lastRoomCreated).format('MMM D, YYYY h:mm A') : '—'}
                </Typography>
                {roomStats.recentRooms?.length > 0 && (
                  <TableContainer sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Room</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Started</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roomStats.recentRooms.map((room) => (
                          <TableRow key={room._id}>
                            <TableCell>{room.name}</TableCell>
                            <TableCell>
                              <Chip label={room.active ? 'Active' : 'Ended'} color={room.active ? 'success' : 'default'} size="small" />
                            </TableCell>
                            <TableCell>{dayjs(room.timeStarted).format('MMM D h:mm A')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            ) : (
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>Loading...</Typography>
            )}
          </InfoCard>
        </Grid>

        {/* Metadata */}
        <Grid size={12}>
          <InfoCard title="Metadata">
            {metadataMutation.isError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {metadataMutation.error?.message || 'Failed to update metadata'}
              </Alert>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Key</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(editableMetadata).map(([key, val]) => (
                    <TableRow key={key}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{key}</TableCell>
                      <TableCell>
                        {editingKey === key ? (
                          <TextField
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            size="small"
                            fullWidth
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleConfirmEdit(key);
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontSize: '0.85rem', cursor: 'pointer' }}
                            onClick={() => handleStartEdit(key)}
                          >
                            {formatValue(val) || <em>empty</em>}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {editingKey === key ? (
                          <>
                            <IconButton size="small" onClick={() => handleConfirmEdit(key)} color="success">
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={handleCancelEdit}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton size="small" onClick={() => handleStartEdit(key)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteKey(key)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {Object.keys(editableMetadata).length === 0 && !showAddRow && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" sx={{
                          color: "text.secondary"
                        }}>No metadata</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {showAddRow && (
                    <TableRow>
                      <TableCell>
                        <TextField
                          value={newKey}
                          onChange={(e) => setNewKey(e.target.value)}
                          size="small"
                          placeholder="key"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddField();
                            if (e.key === 'Escape') { setShowAddRow(false); setNewKey(''); setNewValue(''); }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={newValue}
                          onChange={(e) => setNewValue(e.target.value)}
                          size="small"
                          fullWidth
                          placeholder="value"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddField();
                            if (e.key === 'Escape') { setShowAddRow(false); setNewKey(''); setNewValue(''); }
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={handleAddField} color="success" disabled={!newKey.trim()}>
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => { setShowAddRow(false); setNewKey(''); setNewValue(''); }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShowAddRow(true)}
                disabled={showAddRow}
              >
                Add Field
              </Button>
              <Box sx={{ flex: 1 }} />
              {isDirty && (
                <Button size="small" onClick={handleResetMetadata}>
                  Reset
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                onClick={handleSaveMetadata}
                disabled={!isDirty || metadataMutation.isPending}
              >
                {metadataMutation.isPending ? 'Saving...' : 'Save Metadata'}
              </Button>
            </Box>
          </InfoCard>
        </Grid>

        {/* Free Minutes */}
        <Grid size={12}>
          <InfoCard title="Free Minutes">
            {freeMinutes ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Allocated</TableCell>
                      <TableCell align="right">Used This Month</TableCell>
                      <TableCell align="right">Remaining</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {['participant', 'recording', 'composition', 'streaming', 'transcription'].map((type) => (
                      <TableRow key={type}>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{type}</TableCell>
                        <TableCell align="right">{freeMinutes.allocated?.[type] || 0}</TableCell>
                        <TableCell align="right">{freeMinutes.usedThisMonth?.[type] || 0}</TableCell>
                        <TableCell align="right">{freeMinutes.remaining?.[type] || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>No free minutes allocated</Typography>
            )}
          </InfoCard>
        </Grid>
      </Grid>
      {/* Trial Extension Dialog */}
      <Dialog open={trialDialogOpen} onClose={() => setTrialDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Extend Trial</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Extending trial for {user.name || user.email}
            </Typography>
            <TextField
              label="Extension Days"
              type="number"
              value={extensionDays}
              onChange={(e) => setExtensionDays(parseInt(e.target.value, 10) || 0)}
              fullWidth
              slotProps={{
                htmlInput: { min: 1 }
              }}
            />
            <TextField
              label="Reason"
              value={extensionReason}
              onChange={(e) => setExtensionReason(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrialDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => extendTrialMutation.mutate({
              userId: user._id,
              extensionDays,
              reason: extensionReason,
            })}
            disabled={extendTrialMutation.isPending || extensionDays < 1}
          >
            {extendTrialMutation.isPending ? 'Extending...' : 'Extend'}
          </Button>
        </DialogActions>
        {extendTrialMutation.isError && (
          <Alert severity="error" sx={{ mx: 3, mb: 2 }}>
            {extendTrialMutation.error?.message || 'Failed to extend trial'}
          </Alert>
        )}
      </Dialog>
    </Box>
  );
}

export default AdminUserDetails;
